import * as React from 'react';
import 'react-datepicker/dist/react-datepicker.css';
import * as Yup from 'yup';
import i18next from 'i18next';
import { AnyAction } from 'redux';
import { ThunkDispatch } from 'redux-thunk';
import { connect } from 'react-redux';
import { Card } from 'bloomer';
import { Formik, Form, Field, FormikHelpers, FormikErrors } from 'formik';
import { GetBarcodeFormPayload, Barcode, BarcodeKitStatus } from '../../../models/barcode';
import { CreateProfileResult, Tag } from '../../../models/profile';
import { KitActivateFormPayload } from '../../../models/kit';
import { stripHypen } from '../../../helpers/helpers';
import { FormText, ButtonContainer, KitActivationTitle } from '../../../styles/styled-components/Form/FormComponent';
import FormError from '../../../components/FormError/FormError';
import { FormField } from '../../../components/FormField/FormField';
import { AppState } from '../../../store';
import { actionGetBarcodeDetails } from '../../../store/barcode/action';
import { actionActivateKit } from '../../../store/kit/action';
import styled from 'styled-components';
import { getPartner, getCustomerID } from '../../../constants';
import { Kit, Profile } from '../../../models/customerOverview';
import { LoadingIndicator } from '../../../components/LoadingIndicator/LoadingIndicator';
import HealthService from '../../../store/health/effects';

interface KitActivationStates {
  loader: boolean;
  barcode: string;
  isValidBarcode?: boolean;
}
type KitActivationPopupProps = {
  validateBarcode?: (payload: GetBarcodeFormPayload) => void;
  activateKit?: (payload: KitActivateFormPayload) => void;
  oldBarcode: string;
  barcode?: Barcode;
  profile?: CreateProfileResult;
  hidePopup: any;
  owner?: string;
  customerDetails?: {
    kit: Kit | undefined;
    profile: Profile | undefined;
    pdfLink: string;
  };
};
class KitActivationStatesPopup extends React.PureComponent<KitActivationPopupProps, KitActivationStates> {
  state: KitActivationStates = {
    loader: false,
    barcode: '',
    isValidBarcode: false,
  };
  async handleSubmit(values: KitActivationStates, actions: FormikHelpers<KitActivationStates>) {
    if (this.state.isValidBarcode) {
      if (values.barcode) {
        actions.setStatus({ error: '' });
        this.setState({ loader: true });
        const { customerDetails, activateKit } = this.props;
        if (activateKit && customerDetails) {
          const { profile } = customerDetails;
          const partner = getPartner();
          if (profile) {
            await activateKit({ code: stripHypen(values.barcode), profileId: profile.profileId, partner, owner: this.props.owner });
          }
          const tag: Tag = {
            tag: `replaced:${this.props.oldBarcode}`,
          };
          await HealthService.updateTag({
            isName: false,
            isHeath: false,
            isPhone: false,
            profileId: profile?.profileId || '',
            profile: {
              profile: {
                tag,
              },
            },
            partner: getPartner(),
            owner: getCustomerID(),
          });
          this.setState({ loader: false });
          this.props.hidePopup(stripHypen(values.barcode));
        }
      } else {
        this.setState({ loader: false });
        actions.setStatus({ error: 'form.generic.error' });
      }
    }
  }
  async validateBarcodeHandler(e: React.ChangeEvent<HTMLInputElement>, setFieldTouched: any, setErrors: any, prevErrors: FormikErrors<KitActivationStates>) {
    setFieldTouched('barcode', true);
    await this.validateBarcodes(e.target.value, setErrors, prevErrors);
  }
  async handleFormatBarcode(e: React.ChangeEvent<HTMLInputElement>, setFieldValue: any, setFieldTouched: any, setErrors: any, prevErrors: FormikErrors<KitActivationStates>) {
    setFieldValue('barcode', this.formatBarcode(e.target.value));
    await this.validateBarcodes(this.formatBarcode(e.target.value), setErrors, prevErrors);
  }
  formatBarcode(value: string) {
    const regex = new RegExp('.{1,4}', 'g');
    let val = stripHypen(value);
    if (val.length) {
      val = val.match(regex)!.join('-');
    }
    return val;
  }
  async validateBarcodes(value: string, setErrors: any, prevErrors: FormikErrors<KitActivationStates>) {
    if (value && value.length >= 17) {
      const { validateBarcode } = this.props;
      if (validateBarcode) {
        await validateBarcode({ barcode: stripHypen(value) });
        const { barcode } = this.props;
        if (barcode) {
          if (barcode.error || (this.props.barcode && [BarcodeKitStatus.ORDERED, BarcodeKitStatus.LAB].includes(barcode.status) === false)) {
            setErrors({ ...prevErrors, barcode: 'form.barcode.error.invalid' });
          } else {
            this.setState({ isValidBarcode: true });
          }
        } else {
          this.setState({ isValidBarcode: true });
        }
      }
    } else {
      setErrors({ ...prevErrors, barcode: 'form.barcode.error.incorrect14' });
    }
  }

  render() {
    const validationSchema = Yup.object().shape({
      barcode: Yup.string().min(17, 'form.barcode.error.incorrect14').max(17, 'form.barcode.error.invalid').required('form.barcode.error.empty'),
    });
    return (
      <Card>
        {
          this.state.loader ? <LoadingIndicator show /> :
            <Formik initialValues={this.state}
              onSubmit={(values, actions) => this.handleSubmit(values, actions)}
              validateOnChange={true}
              validationSchema={validationSchema}
              render={({ values, errors, touched, status, setFieldValue, setFieldTouched, setErrors }) => (
                <Form method="POST">
                  <ActivationContainer>
                    <KitActivationTitle>Activate Kit</KitActivationTitle>
                    <FormText />
                    <FormError data={status} />
                    <FormField name="barcode" label={i18next.t('cmp.table.title.barcode')} >
                      <Field name="barcode" className="input" maxLength={200} placeholder={i18next.t('form.barcode.placeholder')} onChange={(e: React.ChangeEvent<HTMLInputElement>) => this.handleFormatBarcode(e, setFieldValue, setFieldTouched, setErrors, errors)} onBlur={(e: React.ChangeEvent<HTMLInputElement>) => this.validateBarcodeHandler(e, setFieldTouched, setErrors, errors)} />
                    </FormField>
                    <ButtonContainer>
                      <div><button className="button" onClick={() => this.closePopUp()}>{i18next.t('transfer.cancelButton')}</button></div>
                      <div><button className="button" type="submit">{i18next.t('dcm.activateButton')}</button></div>
                    </ButtonContainer>
                  </ActivationContainer>
                </Form>
              )}
            />
        }
      </Card>
    );
  }
  closePopUp = () => {
    this.props.hidePopup && this.props.hidePopup();
  };

}

const ActivationContainer = styled.div`
  width:80%;
  margin-left:60px;
`;
const mapStateToProps = (state: AppState, ownProps: KitActivationPopupProps) => {
  return {
    customerDetails: state.customerDetails,
    profile: state.profile,
    barcode: state.barcode,
  };
};

const mapDispatchToProps = (dispatch: ThunkDispatch<any, any, AnyAction>) => {
  return {
    validateBarcode: (payload: GetBarcodeFormPayload) => dispatch(actionGetBarcodeDetails(payload)),
    activateKit: (payload: KitActivateFormPayload) => dispatch(actionActivateKit(payload)),
  };
};

export default connect(mapStateToProps, mapDispatchToProps)(KitActivationStatesPopup);